import { useState } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid2';
import { Table, IconButton, TableRow, TableHead, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DicomViewer from 'components/DicomViewer';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import {
  ActionGrid,
  StudyListGrid,
  StudyListTableContainer,
  ViewportGrid,
  StudyListTableHeadCell,
  StudyListTableBody,
  StudyListTableBodyRow,
  StudyListTableBodyCellLight,
  StudyListPaper,
} from './styles';
import logger from 'utils/logger';
import { standardizeHttpsUrl } from 'components/DicomViewer/utils';

const propTypes = {
  isKenshin: PropTypes.bool.isRequired,
  dicomFilePaths: PropTypes.oneOfType([
    PropTypes.array.isRequired,
    PropTypes.object.isRequired,
  ]),
  close: PropTypes.func.isRequired,
};

const log = logger.createLogger('IssueDetailsViewport');

const IssueDetailsViewport = ({ dicomFilePaths, close, isKenshin }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleClickRow = (_, newSelectedIndex) => {
    setSelectedIndex(newSelectedIndex);
  };

  const series = [];
  const keys = [];
  const tableRowData = [];
  for (let key in dicomFilePaths) {
    const value = dicomFilePaths[key];
    series.push(value.seriesNumber);
    keys.push(key);
    tableRowData.push({
      seriesNumber: value.seriesNumber ? value.seriesNumber : '',
      protocol: value.protocolName ? value.protocolName : '',
      modality: value.modality ? value.modality : '',
      bodypart: value.bodypart ? value.bodypart : '',
      seriesInstanceUID: value.seriesInstanceUID ? value.seriesInstanceUID : '',
      studyInstanceUID: value.studyInstanceUID ? value.studyInstanceUID : '',
      errorFilePaths: value.errorFilePaths ? value.errorFilePaths : [],
    });
  }

  const isSelectedBackgorundColor = (isRowSelected) => {
    return isRowSelected ? 'royalblue' : 'grey';
  };

  const constructTooltipMessage = (errorFilePaths) => {
    return errorFilePaths.reduce((acc, filePath) => {
      const errorCode = filePath.error;
      const errorSopInstanceUID = filePath.sopInstanceUID;
      if (!(errorCode in acc)) acc[errorCode] = [];
      acc[errorCode].push(errorSopInstanceUID);
      return acc;
    }, {});
  };

  // Ensure imageIds are properly formatted
  const imageIds = isKenshin
    ? dicomFilePaths.map((path) => {
        return standardizeHttpsUrl(path);
      })
    : dicomFilePaths[keys[selectedIndex]]?.filePaths.map((path) => {
        return standardizeHttpsUrl(path);
      });

  return (
    <Grid
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        width: '80vw',
      }}>
      <ActionGrid container columns={{ xs: 12 }} justifyContent="flex-end">
        <div>
          <IconButton onClick={close}>
            <CloseIcon sx={{ color: '#fff' }} />
          </IconButton>
        </div>
      </ActionGrid>
      <ViewportGrid columns={{ xs: 12 }}>
        <DicomViewer images={imageIds} />
      </ViewportGrid>
      {/* Grid below the image */}
      {!isKenshin && (
        <StudyListGrid columns={{ xs: 12 }}>
          <StudyListPaper>
            <StudyListTableContainer>
              <Table stickyHeader size="small" aria-label="study list">
                <TableHead>
                  <TableRow>
                    <StudyListTableHeadCell
                      align="center"
                      style={{ width: '5%' }}>
                      {/* Validity column */}
                    </StudyListTableHeadCell>
                    <StudyListTableHeadCell
                      align="center"
                      style={{ width: '30%' }}>
                      {intl.get(
                        'issue_details_viewport_table_header_study_instance_uid'
                      )}
                    </StudyListTableHeadCell>
                    <StudyListTableHeadCell
                      align="center"
                      style={{ width: '30%' }}>
                      {intl.get(
                        'issue_details_viewport_table_header_series_instance_uid'
                      )}
                    </StudyListTableHeadCell>
                    <StudyListTableHeadCell
                      align="center"
                      style={{ width: '10%' }}>
                      {intl.get('issue_details_viewport_table_header_modality')}
                    </StudyListTableHeadCell>
                    <StudyListTableHeadCell
                      align="center"
                      style={{ width: '15%' }}>
                      {intl.get('issue_details_viewport_table_header_bodypart')}
                    </StudyListTableHeadCell>
                  </TableRow>
                </TableHead>
                <StudyListTableBody>
                  {tableRowData.map((rowData, index) => {
                    const isRowSelected =
                      index === selectedIndex ? true : false;
                    return (
                      <StudyListTableBodyRow
                        key={index}
                        hover
                        onClick={(event) => handleClickRow(event, index)}>
                        <StudyListTableBodyCellLight
                          align="center"
                          sx={{
                            background:
                              isSelectedBackgorundColor(isRowSelected),
                          }}
                          style={{ width: '5%' }}>
                          {rowData.errorFilePaths.length > 0 ? (
                            <Tooltip
                              title={
                                <div>
                                  {Object.entries(
                                    constructTooltipMessage(
                                      rowData.errorFilePaths
                                    )
                                  ).map(([errorCode, errorSopInstanceUIDs]) => (
                                    <div key={errorCode}>
                                      {intl.get(`error_code_${errorCode}`)
                                        ? intl.get(`error_code_${errorCode}`)
                                        : errorCode}
                                      : {errorSopInstanceUIDs.join(', ')}
                                    </div>
                                  ))}
                                </div>
                              }
                              enterDelay={500}
                              enterNextDelay={500}>
                              <WarningAmberIcon color="warning" />
                            </Tooltip>
                          ) : (
                            <CheckCircleOutlineIcon color="success" />
                          )}
                        </StudyListTableBodyCellLight>
                        <StudyListTableBodyCellLight
                          align="center"
                          sx={{
                            background:
                              isSelectedBackgorundColor(isRowSelected),
                          }}
                          style={{ width: '30%' }}>
                          {rowData.studyInstanceUID}
                        </StudyListTableBodyCellLight>
                        <StudyListTableBodyCellLight
                          sx={{
                            background:
                              isSelectedBackgorundColor(isRowSelected),
                          }}
                          align="center"
                          style={{ width: '30%' }}>
                          {rowData.seriesInstanceUID}
                        </StudyListTableBodyCellLight>
                        <StudyListTableBodyCellLight
                          sx={{
                            background:
                              isSelectedBackgorundColor(isRowSelected),
                          }}
                          align="center"
                          style={{ width: '10%' }}>
                          {rowData.modality}
                        </StudyListTableBodyCellLight>
                        <StudyListTableBodyCellLight
                          sx={{
                            background:
                              isSelectedBackgorundColor(isRowSelected),
                          }}
                          align="center"
                          style={{ width: '15%' }}>
                          {rowData.bodypart}
                        </StudyListTableBodyCellLight>
                      </StudyListTableBodyRow>
                    );
                  })}
                </StudyListTableBody>
              </Table>
            </StudyListTableContainer>
          </StudyListPaper>
        </StudyListGrid>
      )}
    </Grid>
  );
};

IssueDetailsViewport.propTypes = propTypes;

export default IssueDetailsViewport;
